import React, { useEffect, useState } from 'react';
import { TabBaseMark, CloseIcon } from '../shared/icons';
import {
  TABBASE_URL,
  TABBASE_PROMO_KEY,
  TABBASE_PROMO_DELAY_MS,
  hasSeenTabBasePromo,
  claimTabBasePromo,
} from '../../lib/tabbase-promo';
import { cn } from '../../lib/utils';

/**
 * One-time card announcing TabBase. Appears after `TABBASE_PROMO_DELAY_MS` on the page,
 * at most once per browser (see `src/lib/tabbase-promo.js` for how that is guaranteed).
 * Non-modal: it sits under the header, doesn't take focus, the page stays usable, and
 * Escape, the X or "No thanks" closes it.
 */
export default function TabBasePromo({ delayMs = TABBASE_PROMO_DELAY_MS }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (hasSeenTabBasePromo()) return undefined;
    const timer = setTimeout(() => {
      if (claimTabBasePromo()) setOpen(true);
    }, delayMs);
    // Another ScaleBase tab showed it first: don't show it here too.
    const onStorage = (e) => {
      if (e.key === TABBASE_PROMO_KEY && e.newValue !== null) clearTimeout(timer);
    };
    window.addEventListener('storage', onStorage);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('storage', onStorage);
    };
  }, [delayMs]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const close = () => setOpen(false);

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="tabbase-promo-title"
      aria-describedby="tabbase-promo-body"
      className={cn(
        'fixed z-50 top-[60px] left-2 right-2 lg:top-[76px] lg:left-auto lg:right-5 lg:w-[360px]',
        'rounded-xl border border-line bg-tile text-ink shadow-lg',
        'p-4 pr-3',
      )}
    >
      <div className="flex items-start gap-3">
        <TabBaseMark height={30} className="mt-1" />
        <div className="min-w-0 flex-1">
          <h2 id="tabbase-promo-title" className="text-[16px] font-extrabold leading-snug">
            New: TabBase
          </h2>
          <p id="tabbase-promo-body" className="mt-1 text-[14px] leading-snug text-ink-muted">
            Write guitar tab fast. Get an idea down on your phone in seconds, tidy it up later.
            Free, from the makers of ScaleBase.
          </p>
        </div>
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className={cn(
            '-mt-2 -mr-1 w-11 h-11 shrink-0 flex items-center justify-center rounded-[6px]',
            'text-ink-muted hover:text-ink hover:bg-root/10 transition-colors',
            'outline-none focus-visible:ring-2 focus-visible:ring-root',
          )}
        >
          <CloseIcon />
        </button>
      </div>
      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={close}
          className={cn(
            'h-11 px-3 rounded-lg text-[15px] font-semibold text-ink-muted',
            'hover:text-ink hover:bg-root/10 transition-colors',
            'outline-none focus-visible:ring-2 focus-visible:ring-root',
          )}
        >
          No thanks
        </button>
        <a
          href={TABBASE_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={close}
          className={cn(
            'h-11 px-4 flex items-center rounded-lg bg-root text-primary-foreground',
            'text-[15px] font-bold hover:bg-root/90 transition-colors',
            'outline-none focus-visible:ring-2 focus-visible:ring-root focus-visible:ring-offset-2 focus-visible:ring-offset-tile',
          )}
        >
          Try TabBase
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
      </div>
    </div>
  );
}
